import { makeStyles } from '@material-ui/styles'
import StepSlider from 'core/components/StepSlider'
import TextField from 'core/components/validatedForm/TextField'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import Text from 'core/elements/Text'
import HintTooltip from 'core/elements/tooltip/HintTooltip'
import { ParamsUpdaterGetter } from 'core/hooks/useParams'
import Theme from 'core/themes/model'
import { EvacuationSpeed } from './model'

const speedOptions = [
  { value: EvacuationSpeed.Conservative, label: 'Conservative' },
  { value: EvacuationSpeed.Balanced, label: 'Balanced' },
  { value: EvacuationSpeed.Aggressive, label: 'Aggressive' },
]

const speedDetails = {
  [EvacuationSpeed.Conservative]: {
    description:
      'Most cautious approach. PCD waits through brief outages before evacuating, so a host that recovers on its own is never unnecessarily evacuated. Best for stable environments.',
    timeEstimate: 'Approximate time to first restart: ~10 min',
  },
  [EvacuationSpeed.Balanced]: {
    description:
      'Balances speed and safety. PCD confirms failure before evacuating, minimizing false positives while keeping recovery time reasonable.',
    timeEstimate: 'Approximate time to first restart: ~5 min',
  },
  [EvacuationSpeed.Aggressive]: {
    description:
      'Fastest recovery. PCD evacuates VMs quickly after a host stops responding. Recommended when uptime is critical. Higher risk of false-positive evacuation.',
    timeEstimate: 'Approximate time to first restart: ~2 min',
  },
}

const useStyles = makeStyles<Theme>((theme) => ({
  card: {
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(2),
    display: 'grid',
    gap: theme.spacing(2),
  },
  storageHeartbeatCard: {
    border: `1px solid ${theme.palette.grey[200]}`,
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(2),
    display: 'grid',
    gap: theme.spacing(2),
  },
  customLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  customLabelTitle: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
  },
}))

interface Props {
  value: EvacuationSpeed
  onChange: (value: EvacuationSpeed) => void
  className?: string
  params: {
    storageHeartbeatEnabled: boolean
    nfsHeartbeatPath: string
  }
  getParamsUpdater: ParamsUpdaterGetter<Props['params']>
}

export default function EvacuationSpeedSlider({
  value,
  onChange,
  className,
  params,
  getParamsUpdater,
}: Props) {
  const classes = useStyles()

  return (
    <div
      className={`${classes.card}${className ? ` ${className}` : ''}`}
      data-testid="evacuation-speed-slider"
    >
      <StepSlider
        options={speedOptions}
        details={speedDetails}
        value={value}
        onChange={(v) => onChange(v as EvacuationSpeed)}
        label="Evacuation speed"
        tooltip="When a host stops responding, PCD confirms it is actually down before restarting its VMs elsewhere. Evacuation speed configures how long PCD waits before evacuating VMs. A shorter wait recovers workloads faster but is likelier to react to a brief network blip or a routine reboot."
        subtitle="How quickly PCD evacuates VMs after a host stops responding."
      />
      <div className={classes.storageHeartbeatCard}>
        <ToggleSwitchField
          id="storageHeartbeatEnabled"
          label="Storage Heartbeat"
          value={params.storageHeartbeatEnabled}
          onChange={(val: boolean) => getParamsUpdater('storageHeartbeatEnabled')(val)}
          customLabel={
            <div className={classes.customLabel}>
              <Text variant="caption1" className={classes.customLabelTitle}>
                Storage Heartbeat
                <HintTooltip message="Each host writes a liveness heartbeat to shared storage and watches its peers' heartbeats. If a host drops off the network but keeps updating its heartbeat, PCD treats it as isolated and holds off evacuating it, preventing split-brain data corruption scenarios. If the heartbeat also stops, PCD confirms the failure and recovers faster." />
              </Text>
              <Text variant="body2">
                Adds a second layer of host liveness check through shared storage, helping PCD
                identify a network-isolated host from a failed one. Recommended with Aggressive.
              </Text>
            </div>
          }
        />
        {params.storageHeartbeatEnabled && (
          <TextField
            id="nfsHeartbeatPath"
            label="NFS Heartbeat Path"
            onChange={getParamsUpdater('nfsHeartbeatPath')}
            value={params.nfsHeartbeatPath}
            info="Mount path for shared storage heartbeat check. Must be mounted on all hypervisors in this cluster."
            required
          />
        )}
      </div>
    </div>
  )
}
